// eslint-disable-next-line @typescript-eslint/no-require-imports
const fuzzysort = require("fuzzysort");

interface FuzzyObj {
  _bitflags: number;
  _indexes: number[];
  _nextBeginningIndexes: number[];
  _targetLower: string[];
  _targetLowerCodes: number[];
  score: number;
  target: string;
  obj?: object;
}

export interface Result {
  _indexes: number[];
  score: number;
  segments: {part: string; match?: boolean}[];
  target: string;
}

export interface ObjResult<TObj> {
  obj: TObj;
  results: Record<string, Result>;
}

export interface FuzzyOptions<T = unknown> {
  all?: boolean;
  key?: string | number | ((item: T) => string | number);
  keys?: (string | number)[];
  limit?: number;
  scoreFn?(results: FuzzyObj[]): number;
  threshold?: number;
}

type PreparedSingle = ReturnType<typeof fuzzysort.prepare>;

export class FuzzySort<T extends Object = unknown> {
  private substr = "";
  private fuzzyOpts: FuzzyOptions;
  private readonly prepared: PreparedSingle[];

  constructor(targets: T[] | string[] | {}[], options?: FuzzyOptions) {

    if (options.key) {
      const key = options.key;

      if (["string", "number"].includes(typeof key)) {
        targets = Object.keys(targets).map(tid => targets[tid][key]);
      } else {
        const f = key as (t: T) => string;
        targets = Object.keys(targets).map(tid => f(targets[tid]));
      }
      targets = targets.map(t => fuzzysort.prepare(t));

      if (options.keys) {
        delete options.keys;
      }
    } else if (options.keys) {
      targets = targets.map(t => {
        const target = {};
        options.keys.forEach(k => target[k] = fuzzysort.prepare(t[k]));
        return target;
      });
    } else {
      targets = targets.map(t => fuzzysort.prepare(t));
    }
    this.prepared = targets;

    if (options) {
      this.fuzzyOpts = options;
    }
  }

  get original(): T[] | Record<string, string | number>[] {
    if (this.fuzzyOpts.key) {
      return this.prepared.map(p => p.obj as T);
    } else if (this.fuzzyOpts.keys) {
      return this.prepared.map(p => {
        const obj: Record<string, string | number> = {};
        Object.keys(p).forEach(k => {
          obj[k] = p[k].target;
        });
        return obj;
      });
    } else {
      return this.prepared.map(p => p.target as T);
    }
  }

  get options(): FuzzyOptions {
    const fuzzyOpts: FuzzyOptions = {};
    Object.keys(this.fuzzyOpts).forEach(k => fuzzyOpts[k] = this.fuzzyOpts[k]);
    return fuzzyOpts;
  }

  set options(options: FuzzyOptions) {
    this.fuzzyOpts = options;
  }

  private static refineResult(r: FuzzyObj): Result {
    // I like my version of the indexes, basically create mat segments of the result
    const res = {} as Result;
    res.score = r.score;
    res.target = r.target;
    // eslint-disable-next-line no-underscore-dangle
    res._indexes = r._indexes;
    res.segments = [{part: res.target[0]}];

    // eslint-disable-next-line no-underscore-dangle
    if (res._indexes.includes(0)) {
      res.segments[0].match = true;
    }

    for (let i = 1; i < res.target.length; i++) {
      // eslint-disable-next-line no-underscore-dangle
      if (res._indexes.includes(i)) {
        if (res.segments[res.segments.length - 1].match) {
          res.segments[res.segments.length - 1].part += res.target[i];
        } else {
          res.segments.push({part: res.target[i], match: true});
        }
      } else if (res.segments[res.segments.length - 1].match) {
        res.segments.push({part: res.target[i]});
      } else {
        res.segments[res.segments.length - 1].part += res.target[i];
      }
    }
    return res;
  }

  search(substr: string, options: FuzzyOptions = this.fuzzyOpts): Result[] | ObjResult<unknown>[] {
    substr = !substr && substr !== "" ? this.substr : substr;
    this.substr = substr;

    if (!options.keys) {
      return fuzzysort.go(substr, this.prepared, options).map(r => FuzzySort.refineResult(r));
    } else {
      const r = fuzzysort.go(substr, this.prepared, options);
      const results: ObjResult<unknown>[] = [];
      const limit = options.limit ? Math.min(options.limit, r.length - 1) : r.length - 1;

      for (let idx = 0; idx <= limit; idx++) {
        const objResults: Record<string, Result> = {};
        let obj = null;

        for (let i = 0; i < r[idx].length; i++) {
          if (r[idx][i]) {
            objResults[options.keys[i]] = FuzzySort.refineResult(r[idx][i]);
            if (!obj && r[idx].obj) {
              obj = {};
              Object.keys(r[idx].obj).forEach(k => obj[k] = r[idx].obj[k].target);
            }
          }
        }

        if (Object.keys(objResults).length === 0) {
          break;
        }
        const result: ObjResult<unknown> = {results: objResults, obj};
        results.push(result);
      }
      return results;
    }
  }
}
